import * as React from 'react'

import { ResponsiveScatterPlot } from '@nivo/scatterplot'
import { scaleOrdinal } from 'd3-scale'
import compact from 'lodash/compact'
import groupBy from 'lodash/groupBy'
import mean from 'lodash/mean'
import without from 'lodash/without'

import { observer } from 'mobx-react'

import { RatingDefaultValues } from 'modules/custom_resources/store/collections/customFields/fields'
import type ReportStruct from 'modules/reports/lib//ReportStruct'
import { NINE_BOX } from 'modules/reports/lib/Constants'
import { UIColors, colorList } from 'modules/reports/lib/colors'
import nivoTheme from 'modules/reports/lib/nivoTheme'
import fields from 'modules/reports/store/collections/fields'
import { SCATTERPLOT_PROPS } from 'modules/reports/store/models/Config'
import type Field from 'modules/reports/store/models/Field'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'

interface Props {
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
}

const Scatterplot = ({ data, reportStruct }: Props) => {
  const scale = scaleOrdinal(colorList)
  const projections = reportStruct.props.projections

  const fieldsProjections = projections.filter((projection) => {
    return !!fields.find({
      path: projection.path,
    })
  })

  const groupedProjections = groupBy(fieldsProjections, (projection) => projection.ui_group)

  const xProjectionsPaths = groupedProjections.x?.map((projection) => projection.path) || []

  const yProjectionsPaths = groupedProjections.y?.map((projection) => projection.path) || []

  // NOTE: range is the same for all competence fields. We want to ensure that
  // the projection really exists as a field and the field has a range
  const projection: ProjectionParams | null | undefined = projections.find((projection) => {
    const field = fields.find({
      path: projection.path,
    })

    return field?.attributes.range.length
  })

  const field: Field | null | undefined = fields.find({
    path: projection?.path,
  })

  const range: [number, number] = field?.attributes.range || [
    RatingDefaultValues.minValue,
    RatingDefaultValues.maxValue,
  ]

  const [minValue, maxValue] = range
  const tier = (maxValue - minValue) / 3
  const firstTier = (minValue + tier).toFixed(2)
  const secondTier = (maxValue - tier).toFixed(2)
  const chartValues = [minValue, firstTier, secondTier, maxValue]

  const processedData = data.map((row) => {
    const xValues = xProjectionsPaths.map((path) => row[path])
    const xValue = mean(without(xValues, null))
    const yValues = yProjectionsPaths.map((path) => row[path])
    const yValue = mean(without(yValues, null))

    if (isNaN(xValue) || isNaN(yValue)) return null

    const employeeName = row[NINE_BOX.employee_name_path]

    return {
      id: employeeName,
      data: [
        {
          x: xValue,
          y: yValue,
        },
      ],
    }
  })

  return (
    <ResponsiveScatterPlot
      data={compact(processedData)}
      theme={nivoTheme}
      tooltip={(e) => `${e.node.data.serieId}`}
      xScale={{
        type: 'linear',
        min: minValue,
        max: maxValue,
      }}
      yScale={{
        type: 'linear',
        min: minValue,
        max: maxValue,
      }}
      gridXValues={chartValues}
      gridYValues={chartValues}
      axisLeft={{
        tickValues: chartValues,
      }}
      axisBottom={{
        tickValues: chartValues,
      }}
      colorBy={(item: Record<string, any>) => {
        const value = item.id

        if (!value) return UIColors.grey3

        return scale(value)
      }}
      colors={colorList}
      {...SCATTERPLOT_PROPS}
    />
  )
}

export default observer(Scatterplot)
